import { ReactNode, useState } from "react"
import { useLocation } from "react-router-dom"
import PushPinIcon from "@mui/icons-material/PushPin"
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined"
import Logo from "~components/Logo"
import Header from "~app/Header"
import NavBar from "~app/NavBar"
import ErrorBoundary from "~components/app/ErrorBoundary"
import {
  AppContainer,
  ContentContainer,
  MainContainer,
  NavBarContainer,
  SidebarRail,
  SidebarToggle,
} from "~styled/app"
import SiteVersion from "~components/SiteVersion"

type LayoutProps = {
  children: ReactNode
}

const STORAGE_KEY = "sidebar-pinned"

const Layout = (props: LayoutProps) => {
  const location = useLocation()
  // Pinned = expanded and anchored (reserves space). Unpinned = a 64px rail that
  // expands over the content on hover. `expanded` drives the visual width;
  // `isPinned` drives the reserved rail width (so hovering never reflows content).
  const [isPinned, setIsPinned] = useState(
    () => localStorage.getItem(STORAGE_KEY) === "true",
  )
  const [isHovering, setIsHovering] = useState(false)
  const expanded = isPinned || isHovering

  const togglePin = () => {
    setIsPinned(prev => {
      const next = !prev
      localStorage.setItem(STORAGE_KEY, String(next))
      return next
    })
  }

  return (
    <AppContainer>
      <SidebarRail className={isPinned ? "pinned" : ""}>
        <NavBarContainer
          className={expanded ? "" : "collapsed"}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <Logo className="navbar-logo" />
          <NavBar />
          <SiteVersion />
          <SidebarToggle
            className={isPinned ? "active" : ""}
            onClick={togglePin}
            title={isPinned ? "Unpin sidebar" : "Pin sidebar open"}
          >
            {isPinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
          </SidebarToggle>
        </NavBarContainer>
      </SidebarRail>

      <ContentContainer id="app-content-container">
        <Header />
        <MainContainer>
          <main>
            <ErrorBoundary resetKey={location.pathname}>
              {props.children}
            </ErrorBoundary>
          </main>
        </MainContainer>
      </ContentContainer>
    </AppContainer>
  )
}

export default Layout
